/**
 * Builder > Conditions > Condition pill
 * <ua-builder-conditions-pill>
 */

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { getConditionById, updateRemoteConditionsMeta } from '@builder-legacy/item/conditions/utilities';
import Actions from '@builder-legacy/item/actions';

// Import style for web component
import styles from './web-component-style.scss';

// eslint-disable-next-line require-jsdoc
@customElement( 'ua-builder-conditions-pill' )
export class ConditionPill extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: 'condition-id', reactive: true } ) accessor id;
	@property( { type: String, attribute: 'condition-group-id', reactive: true } ) accessor groupId;

	// The whole pill element
	$pill = createRef();
	// Remove button element
	$removeButton = createRef();

	// eslint-disable-next-line require-jsdoc, no-useless-constructor
	constructor() {
		super();
	}

	/**
	 * Adds the dynamic name when the element is added to the DOM
	 * THIS IS A TEMPORARY SOLUTION, until tokens become web components
	 *
	 * Steps:
	 * 1. Make sure tokens are web components
	 * 2. Move .condition.$dynamicSentence to the Shadow DOM
	 * 3. Remove this method completely
	 *
	 * @return {undefined}
	 */
	connectedCallback() {
		super.connectedCallback();

		// Add dynamic name
		// This should be inside the TemplateResult (in the Shadow DOM), not here
		this.innerHTML = '';
		this.append( this.condition.$dynamicSentence );
	}

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if the condition property is defined
		if ( ! _uo.utility.isDefined( this.condition ) ) {
			return;
		}

		// Check if it's NOT missing
		if ( ! this.condition.isMissing ) {
			return this._templateCondition;
		}

		// Check if it's missing
		if ( this.condition.isMissing ) {
			return this._templateMissingCondition;
		}
	}

	/**
	 * Returns the template a condition
	 *
	 * @return {TemplateResult} The template of the condition
	 */
	get _templateCondition() {
		return html`
			<div class="pill" ${ ref( this.$pill ) }>

				<div class="pill-logo">
					<ua-builder-integration-icon
						code="${ this.condition.integrationCode }"
						size="xsmall"
						plain
					></ua-builder-integration-icon>
				</div>

				<div class="pill-sentence">
					<slot></slot>
				</div>

				<div class="pill-actions">
					<uo-button
						@click=${ this.edit }

						class="pill-actions-edit"
						color="transparent"
						size="small"
					>
						<uo-icon id="pencil"></uo-icon>
					</uo-button>

					<uo-button
						${ ref( this.$removeButton ) }

						@click=${ this.remove }

						class="pill-actions-remove"
						color="transparent"
						size="small"
					>
						<uo-icon id="trash"></uo-icon>
					</uo-button>
				</div>

			</div>
		`;
	}

	/**
	 * Returns the template a condition when it's missing
	 *
	 * @return {TemplateResult} The template of the missing condition
	 */
	get _templateMissingCondition() {
		return html`
			<div class="pill pill--missing" ${ ref( this.$pill ) }>

				<div class="pill-sentence">
					${
						_uo.utility.isDefined( this.condition.backup.integrationName ) ?
							html`
								<uo-alert 
									type="warning"
									heading=${ UncannyAutomator._core.i18n.conditions.missingCondition }
									size="xsmall"
								>
									${ this.condition.backup.integrationName }: <slot></slot>
								</uo-alert>
							` :
							UncannyAutomator._core.i18n.conditions.missingCondition
					}
				</div>

				<div class="pill-actions">

					<uo-button
						${ ref( this.$removeButton ) }

						@click=${ this.remove }

						class="pill-actions-remove"
						color="transparent"
						size="small"
					>
						<uo-icon id="trash"></uo-icon>
					</uo-button>

				</div>

			</div>
		`;
	}

	/**
	 * Edits the condition
	 *
	 * @return {undefined}
	 */
	edit() {
		// Edit condition
		this.condition.conditionsGroup.editCondition( {
			conditionId: this.condition.id,
		} );
	}

	/**
	 * Removes the condition
	 *
	 * @return {undefined}
	 */
	remove() {
		// Force actions to be visible all the time in the pill
		this.$pill.value.classList.add( 'pill--force-visible-actions' );
		// Add loading animation to the remove button
		this.$removeButton.value.loading = true;

		// Remove the condition from the main object
		this.condition.conditionsGroup.removeCondition( {
			conditionID: this.condition.id,
		} );

		// Update the meta
		updateRemoteConditionsMeta( {
			onSuccess: () => {
				new Actions();
			}
		} );
	}

	/**
	 * Returns the condition of this Condition Pill
	 *
	 * @return {Condition} The attached condition
	 */
	get condition() {
		// Return the condition
		return getConditionById( {
			conditionsGroupId: this.groupId,
			conditionId: this.id
		} );
	}
}
